/* global axios */
import ApiClient from './ApiClient';

class AgentBotsAPI extends ApiClient {
  constructor() {
    super('agent_bots', { accountScoped: true });
    this.vowchatApiUrl =
      typeof window !== 'undefined'
        ? window.chatwootConfig?.langbotApiUrl
        : null;
    this.vowchatToken = null;
  }

  // Get current user's Langbot API key
  getCurrentUserApiKey() {
    // Get current user from store
    const currentUser = this.getCurrentUser();
    return currentUser?.langbot_api_key;
  }

  // Get current user from Vuex store
  // This method should not be used directly - pass user from components instead
  getCurrentUser() {
    console.warn(
      'AgentBotsAPI.getCurrentUser() is deprecated. Pass user explicitly to API methods.'
    );
    // Try to get user from window global state as fallback
    if (typeof window !== 'undefined' && window.chatwootConfig?.currentUser) {
      return window.chatwootConfig.currentUser;
    }
    return null;
  }

  // Authenticate with Vowchat API using user_id
  async authenticate(userOverride = null) {
    if (!this.vowchatApiUrl) {
      throw new Error(
        'Langbot API URL not configured. Please contact your administrator.'
      );
    }

    // Require user to be passed explicitly
    if (!userOverride) {
      throw new Error('User must be passed explicitly to authenticate method');
    }

    const currentUser = userOverride;
    if (!currentUser) {
      console.error('Authentication failed: No current user found');
      console.error('userOverride:', userOverride);
      throw new Error('No current user found');
    }

    console.log('Authentication attempt for user:', {
      id: currentUser.id,
      email: currentUser.email,
      external_user_id: currentUser.external_user_id,
      has_langbot_api_key: !!currentUser.langbot_api_key,
    });

    try {
      // Use user_id for authentication like vowchat channels
      const userId = currentUser.external_user_id;
      const apiKey = currentUser.langbot_api_key;

      // Primary authentication method: user_id
      if (userId) {
        const response = await axios.post(
          `${this.vowchatApiUrl}/api/v1/user/auth-by-user-id`,
          {
            user_id: userId,
          }
        );
        // Token is nested in data.data.token
        this.vowchatToken = response.data.data.token;
        return this.vowchatToken;
      }

      // Fallback authentication method: API key
      if (apiKey) {
        // For API key authentication, we'll use it directly as the token
        this.vowchatToken = apiKey;
        return this.vowchatToken;
      }

      throw new Error(
        'No valid authentication method available (no user_id or api_key)'
      );
    } catch (error) {
      console.error('Vowchat authentication failed:', error);

      // If user_id auth failed but we have API key, try that as fallback
      if (error.response?.status === 401 && currentUser.langbot_api_key) {
        console.log('Falling back to API key authentication');
        this.vowchatToken = currentUser.langbot_api_key;
        return this.vowchatToken;
      }

      throw error;
    }
  }

  // Get bots from Vowchat API using JWT authentication
  async getVowchatBots(userOverride = null) {
    // Always use JWT token authentication
    if (!this.vowchatToken) {
      await this.authenticate(userOverride);
    }

    try {
      const response = await axios.get(
        `${this.vowchatApiUrl}/api/v1/platform/bots`,
        {
          headers: {
            Authorization: `Bearer ${this.vowchatToken}`,
          },
        }
      );
      return response;
    } catch (error) {
      // Try to re-authenticate if token expired
      if (error.response?.status === 401) {
        await this.authenticate(userOverride);
        return axios.get(`${this.vowchatApiUrl}/api/v1/platform/bots`, {
          headers: {
            Authorization: `Bearer ${this.vowchatToken}`,
          },
        });
      }
      throw error;
    }
  }

  // Toggle bot enable/disable status using JWT authentication
  async toggleBot(botUuid, enable, userOverride = null) {
    // Always use JWT token authentication
    if (!this.vowchatToken) {
      await this.authenticate(userOverride);
    }

    try {
      const response = await axios.put(
        `${this.vowchatApiUrl}/api/v1/platform/bots/${botUuid}`,
        { enable },
        {
          headers: {
            Authorization: `Bearer ${this.vowchatToken}`,
            'Content-Type': 'application/json',
          },
        }
      );
      return response;
    } catch (error) {
      // Try to re-authenticate if token expired
      if (error.response?.status === 401) {
        await this.authenticate(userOverride);
        return axios.put(
          `${this.vowchatApiUrl}/api/v1/platform/bots/${botUuid}`,
          { enable },
          {
            headers: {
              Authorization: `Bearer ${this.vowchatToken}`,
              'Content-Type': 'application/json',
            },
          }
        );
      }
      throw error;
    }
  }

  // Keep original methods for compatibility
  create(data) {
    return axios.post(this.url, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  update(id, data) {
    return axios.patch(`${this.url}/${id}`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  deleteAgentBotAvatar(botId) {
    return axios.delete(`${this.url}/${botId}/avatar`);
  }

  resetAccessToken(botId) {
    return axios.post(`${this.url}/${botId}/reset_access_token`);
  }
}

export default new AgentBotsAPI();
